import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { trpc } from 'src/utils/trpc';
import { Box } from 'src/ui/box';
import { Button } from 'src/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from 'src/ui/dialog';
import { Input } from 'src/ui/input';
import { TextArea } from 'src/ui/text-area';
import { useMediaQuery } from 'src/ui/hooks/useMediaQuery';
import { theme } from 'src/ui/theme/theme-options';
import { cn } from 'src/utils/@/lib/utils';

interface Props {
  open: boolean;
  handleClose: () => void;
  vehicle: any;
}

const SalesLeadDialog: React.FC<Props> = ({ open, handleClose, vehicle }) => {
  const smBelow = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      message: '',
    },
  });

  const saveMutation = trpc.vehicles.sendLeadEmail.useMutation({
    onError(error) {
      console.error(error);
    },
    onSuccess() {
      toast.success('Dealership has been notified!');

      setTimeout(function () {
        handleClose();
      }, 1000);
    },
  });

  const onSubmit = (data: any) => {
    reset();
    saveMutation.mutate({
      vehicle_id: vehicle.id,
      contact_email: data.email,
      contact_phone: data.phone,
      contact_first_name: data.first_name,
      contact_last_name: data.last_name,
    });
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogContent
        fullScreen={smBelow}
        closeIcon
        title="Check Availability"
        className="sm:min-w-[600px] sm:max-w-2xl"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>Check Availability</DialogTitle>
          <Box className="flex flex-col items-center justify-start p-4">
            <Box className="m-2 w-4/5">
              <Controller
                name="first_name"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <Input
                    value={value}
                    label="First Name"
                    onChange={onChange}
                    id="sales-lead-first-name"
                    className={cn(
                      'h-14',
                      errors.first_name && 'border-destructive'
                    )}
                  />
                )}
              />
              {errors.first_name && (
                <p className="mt-1 text-sm text-destructive">
                  This field is required
                </p>
              )}
            </Box>
            <Box className="m-2 w-4/5">
              <Controller
                name="last_name"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <Input
                    value={value}
                    label="Last Name"
                    onChange={onChange}
                    id="sales-lead-last-name"
                    className={cn(
                      'h-14',
                      errors.last_name && 'border-destructive'
                    )}
                  />
                )}
              />
              {errors.last_name && (
                <p className="mt-1 text-sm text-destructive">
                  This field is required
                </p>
              )}
            </Box>
            <Box className="m-2 w-4/5">
              <Controller
                name="email"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <Input
                    value={value}
                    label="Email"
                    onChange={onChange}
                    id="sales-lead-email"
                    className={cn(
                      'h-14',
                      errors.email && 'border-destructive'
                    )}
                  />
                )}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-destructive">
                  This field is required
                </p>
              )}
            </Box>
            <Box className="m-2 w-4/5">
              <Controller
                name="phone"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <Input
                    value={value}
                    label="Phone Number"
                    onChange={onChange}
                    id="sales-lead-phone"
                    className={cn(
                      'h-14',
                      errors.phone && 'border-destructive'
                    )}
                  />
                )}
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-destructive">
                  This field is required
                </p>
              )}
            </Box>
            <Box className="m-2 w-4/5">
              <Controller
                name="message"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextArea
                    value={value}
                    label="Message"
                    onChange={onChange}
                    id="sales-lead-message"
                    placeholder=" "
                    fullWidth
                    error={Boolean(errors.message)}
                    helperText={
                      errors.message ? 'This field is required' : undefined
                    }
                  />
                )}
              />
            </Box>
          </Box>
          <DialogFooter>
            <Button
              type="button"
              variant="contained"
              color="error"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="success">
              Submit
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SalesLeadDialog;
